import Link from 'next/link';
import {
  Formats,
  MarkupTranslationValues,
  RichTranslationValues,
  TranslationValues,
} from 'next-intl';
import React, {ReactNode} from 'react';
import {createTranslator} from 'use-intl/core';
import {LOCALE_LIST} from '../locale/constant';
import ko from '../locale/ko';
import {LocaleType, TranslateKey} from '../locale/type';
import {requestContext} from './requestContext';
import {twMerge} from './twMerge';
import {TextTKey} from './type';

const LOCALE_TRANSLATOR_MAP = LOCALE_LIST.reduce(
  (obj, locale) => {
    const {default: localeFile} = require(`../locale/${locale}`);
    const translator = createTranslator({
      locale,
      messages: localeFile,
    });
    obj[locale] = translator;
    return obj;
  },
  {} as {[key in LocaleType]: ReturnType<typeof createTranslator>}
);

function t<
  T extends TranslateKey,
  O extends {
    needTranslate?: boolean;
    props?: {className?: string; [key: string]: any};
  } = {},
>(
  _key: T,
  params?: {
    values?: TranslationValues;
    formats?: Formats;
    options?: O;
  }
): O extends {needTranslate: false}
  ? string
  : (typeof ko)[T]['needTranslate'] extends true
    ? ReactNode
    : string {
  const locale =
    requestContext.requestInfo?.lang.queryDeviceLang ??
    requestContext.requestInfo?.lang.deviceLang ??
    'ko';
  const _t = LOCALE_TRANSLATOR_MAP[locale];

  if (!_t?.has(`${_key}.value`)) return _key;

  const props = params?.options?.props;

  let key = _key;

  if (
    _t.has(`${_key}_plural`) &&
    Number.isInteger(params?.values?.value) &&
    (params?.values?.value as number) > 1
  ) {
    key = `${_key}_plural` as T;
  }

  // @TODO: 추후 Full-TC를 돌 때 개행문자가 포함된 문자열에 대한 스타일 처리를 일괄 적용할 수 있도록
  // Text 컴포넌트에 whitespace-pre-line 스타일을 추가하고 모든 텍스트 영역에서 Text 컴포넌트를 사용하도록 수정해야 합니다.
  if (params?.options?.needTranslate ?? ko[key].needTranslate) {
    const formatters: RichTranslationValues = {
      bold: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('font-bold', props?.className)},
          child
        ),
      EL_BC: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('text-el-bc', props?.className)},
          child
        ),
      link: child => React.createElement('a', props, child),
      link_terms: child =>
        React.createElement(
          Link,
          {
            href: params?.values?.link_terms_href as string,
            prefetch: false,
            ...props,
          },
          child
        ),
      link_privacy: child =>
        React.createElement(
          Link,
          {
            href: params?.values?.link_privacy_href as string,
            prefetch: false,
            ...props,
          },
          child
        ),
      S_Red: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('text-s-red', props?.className)},
          child
        ),
      S_Green: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('text-s-green', props?.className)},
          child
        ),
      S_Blue: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('text-s-blue', props?.className)},
          child
        ),
      EL_60: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('text-el-60', props?.className)},
          child
        ),
      EL_70: child =>
        React.createElement(
          'span',
          {...props, className: twMerge('text-el-70', props?.className)},
          child
        ),
      'EL_70-bold': child =>
        React.createElement(
          'span',
          {
            ...props,
            className: twMerge('text-el-70 font-bold', props?.className),
          },
          child
        ),
    };

    // @ts-ignore
    return _t.rich(
      `${key}.value`,
      {...formatters, ...params?.values},
      params?.formats
    );
  }

  const stringFormatters: MarkupTranslationValues = {
    bold: child => `<bold>${child}</bold>`,
    EL_BC: child => `<EL_BC>${child}</EL_BC>`,
    link: child => `<link>${child}</link>`,
    S_Red: child => `<S_Red>${child}</S_Red>`,
    S_Green: child => `<S_Green>${child}</S_Green>`,
    S_Blue: child => `<S_Blue>${child}</S_Blue>`,
    EL_60: child => `<EL_60>${child}</EL_60>`,
    EL_70: child => `<EL_70>${child}</EL_70>`,
    'EL_70-bold': child => `<EL_70-bold>${child}</EL_70-bold>`,
  };

  return _t.markup(
    `${key}.value`,
    {...stringFormatters, ...params?.values},
    params?.formats
  );
}

function tt(textWithTKey?: TextTKey) {
  if (!textWithTKey) {
    return '';
  }

  if (textWithTKey.textTKey) {
    return t(textWithTKey.textTKey);
  }
  return textWithTKey.text;
}

export {t, tt};
